import React, { useState, useEffect } from "react";

// ─────────────────────────────────────────────
// BRAND / THEME (v0 palette — Argon style)
// ─────────────────────────────────────────────
const T = {
  bg:"#F8F9FA", white:"#FFFFFF", navy:"#1A2332", blue:"#4318FF", cyan:"#0075FF",
  teal:"#01B574", gold:"#FFB547", text:"#2D3748", ghost:"#A0AEC0", border:"#E2E8F0",
  input:"#F4F7FE", red:"#E31A1A", orange:"#FF6B00",
};

// ─────────────────────────────────────────────
// PERSISTENCE
// ─────────────────────────────────────────────
const LS = {
  get: (k) => { try { const v = localStorage.getItem("sharkos_"+k); return v ? JSON.parse(v) : null; } catch { return null; } },
  set: (k,v) => { try { localStorage.setItem("sharkos_"+k, JSON.stringify(v)); } catch {} },
};
async function ipc(channel, ...args) {
  if (window.sharkOS?.ipcRenderer) return window.sharkOS.ipcRenderer.invoke(channel, ...args);
  const mock = {
    "get-clients":        () => { const v=LS.get("clients"); return Array.isArray(v)?v:[]; },
    "save-client":        (c) => { const v=LS.get("clients"); const cs=Array.isArray(v)?v:[]; const i=cs.findIndex(x=>x.id===c.id); i>=0?cs.splice(i,1,c):cs.unshift(c); LS.set("clients",cs); return c; },
    "delete-client":      (id) => { const v=LS.get("clients"); const cs=Array.isArray(v)?v:[]; LS.set("clients",cs.filter(x=>x.id!==id)); return true; },
    "get-settings":       () => LS.get("settings") || {},
    "save-settings":      (s) => { LS.set("settings",s); return s; },
    "get-checklist":      (cid) => LS.get("checklist_"+cid) || {},
    "save-checklist":     ({clientId,state}) => { LS.set("checklist_"+clientId,state); return true; },
    "get-report-history": (cid) => LS.get("reports_"+cid) || [],
    "save-report":        ({clientId,report}) => { const rs=LS.get("reports_"+clientId)||[]; rs.unshift(report); LS.set("reports_"+clientId,rs); return report; },
    "run-audit":          () => new Promise(r=>setTimeout(()=>r({score:Math.floor(Math.random()*40)+55}),1800)),
    "gmb-sync":           () => new Promise(r=>setTimeout(()=>r({name:"Synced",phone:"(555) 000-0000",address:"Tampa Bay, FL",verified:true}),1200)),
    "deploy-gtm":         () => new Promise(r=>setTimeout(()=>r({success:true}),500)),
    "inject-schema":      () => new Promise(r=>setTimeout(()=>r({success:Math.random()>0.12}),800+Math.random()*500)),
    "generate-report":    () => new Promise(r=>setTimeout(()=>r({path:"report_"+Date.now()+".pdf"}),1500)),
  };
  const fn = mock[channel]; return fn ? fn(...args) : null;
}

// ─────────────────────────────────────────────
// â”€â”€ Browser GEO Audit Engine â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
async function fetchProxy(url) {
  try {
    const r = await fetch("https://api.allorigins.win/get?url=" + encodeURIComponent(url), { signal: AbortSignal.timeout(7000) });
    if (!r.ok) return null;
    const d = await r.json();
    return d.contents || null;
  } catch { return null; }
}

async function geoCheckRobots(domain) {
  const CRAWLERS = [
    {name:"GPTBot",tier:1,owner:"OpenAI"},{name:"OAI-SearchBot",tier:1,owner:"OpenAI"},
    {name:"ClaudeBot",tier:1,owner:"Anthropic"},{name:"PerplexityBot",tier:1,owner:"Perplexity"},
    {name:"Googlebot",tier:1,owner:"Google"},{name:"Google-Extended",tier:2,owner:"Google"},
    {name:"Applebot-Extended",tier:2,owner:"Apple"},{name:"Amazonbot",tier:2,owner:"Amazon"},
    {name:"FacebookBot",tier:2,owner:"Meta"},{name:"bingbot",tier:2,owner:"Microsoft"},
    {name:"CCBot",tier:3,owner:"Common Crawl"},{name:"Bytespider",tier:3,owner:"ByteDance"},
  ];
  if (!domain) return { findings:[], crawlerMap:[], score:0, maxScore:40 };
  const clean = domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
  const text = await fetchProxy("https://" + clean + "/robots.txt");
  const crawlerMap = CRAWLERS.map(c => {
    if (!text) return {...c, allowed:null, status:"Unknown"};
    const lower = text.toLowerCase();
    const idx = lower.indexOf("user-agent: " + c.name.toLowerCase());
    if (idx === -1) return {...c, allowed:true, status:"Allowed"};
    const section = text.substring(idx, idx + 400);
    const blocked = /disallow:\s*\//i.test(section);
    return {...c, allowed:!blocked, status:blocked?"Blocked":"Allowed"};
  });
  const blocked = crawlerMap.filter(c => c.status === "Blocked");
  const t1blocked = blocked.filter(c => c.tier === 1);
  const findings = [];
  if (!text) {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Warning",detail:"robots.txt not found",impact:"-8 pts"});
    return { findings, crawlerMap, score:15, maxScore:40 };
  }
  if (t1blocked.length > 0) {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Fail",
      detail:"Critical AI crawlers blocked: " + t1blocked.map(c=>c.name).join(", "),impact:"-" + (t1blocked.length*8) + " pts"});
  } else if (blocked.length > 0) {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Warning",
      detail:"Some crawlers blocked: " + blocked.map(c=>c.name).join(", "),impact:"-4 pts"});
  } else {
    findings.push({platform:"Crawler Access",category:"robots.txt",status:"Pass",
      detail:"All AI crawlers have access",impact:"+10 pts"});
  }
  const score = Math.max(0, 40 - t1blocked.length*8 - (blocked.length - t1blocked.length)*3);
  return { findings, crawlerMap, score, maxScore:40 };
}

async function geoCheckLlms(domain, clientName) {
  if (!domain) return { findings:[], llmsData:null, generatedFile:null, score:0, maxScore:20 };
  const clean = domain.replace(/^https?:\/\//,"").replace(/\/$/,"");
  const text = await fetchProxy("https://" + clean + "/llms.txt");
  const findings = [];
  const isHtml = text && (text.trimStart().startsWith("<!") || text.trimStart().startsWith("<html"));
  if (!text || text.length < 50 || isHtml) {
    const generated = `# ${clientName||"Business"}\n\n> AI-readable summary for language models.\n\n## About\n${clientName||"This business"} serves customers in the local area.\n\n## Services\n- [Add your primary services here]\n\n## Contact\n- Website: https://${clean}\n`;
    findings.push({platform:"llms.txt",category:"AI Readability",status:"Fail",
      detail:"No llms.txt found â€” AI models cannot self-describe this business",impact:"-10 pts"});
    return { findings, llmsData:null, generatedFile:generated, score:0, maxScore:20 };
  }
  const hasAbout = /##\s*about/i.test(text);
  const hasServices = /##\s*service/i.test(text);
  const score = (hasAbout ? 10 : 0) + (hasServices ? 10 : 0);
  if (hasAbout && hasServices) {
    findings.push({platform:"llms.txt",category:"AI Readability",status:"Pass",
      detail:"llms.txt present with About and Services sections",impact:"+20 pts"});
  } else {
    findings.push({platform:"llms.txt",category:"AI Readability",status:"Warning",
      detail:"llms.txt found but missing " + (!hasAbout?"About ":"") + (!hasServices?"Services":"") + " section",impact:"+"+score+" pts"});
  }
  return { findings, llmsData:text, generatedFile:null, score, maxScore:20 };
}

async function geoCheckWikipedia(clientName) {
  const findings = [];
  if (!clientName) return { findings, wikiUrl:null, score:0, maxScore:20 };
  try {
    const query = encodeURIComponent(clientName);
    const r = await fetch(`https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${query}&format=json&origin=*`, { signal: AbortSignal.timeout(6000) });
    const d = await r.json();
    const hits = d?.query?.search || [];
    const match = hits.find(h => h.title.toLowerCase().includes(clientName.toLowerCase().split(" ")[0]));
    if (match) {
      findings.push({platform:"Brand Authority",category:"Wikipedia",status:"Pass",
        detail:"Wikipedia entry found: " + match.title,impact:"+20 pts"});
      return { findings, wikiUrl:"https://en.wikipedia.org/wiki/" + encodeURIComponent(match.title), score:20, maxScore:20 };
    }
  } catch {}
  findings.push({platform:"Brand Authority",category:"Wikipedia",status:"Fail",
    detail:"No Wikipedia entry found â€” low entity authority for AI models",impact:"-10 pts"});
  return { findings, wikiUrl:null, score:0, maxScore:20 };
}

async function runGeoAudit(client) {
  const domain = client.domain || "";
  const name = client.name || "";
  const [robots, llms, wiki] = await Promise.allSettled([
    geoCheckRobots(domain),
    geoCheckLlms(domain, name),
    geoCheckWikipedia(name),
  ]);
  const r = robots.status==="fulfilled" ? robots.value : {findings:[],crawlerMap:[],score:0,maxScore:40};
  const l = llms.status==="fulfilled"   ? llms.value   : {findings:[],llmsData:null,generatedFile:null,score:0,maxScore:20};
  const w = wiki.status==="fulfilled"   ? wiki.value   : {findings:[],wikiUrl:null,score:0,maxScore:20};
  return {
    crawlerMap: r.crawlerMap,
    llmsGenerated: l.generatedFile,
    llmsData: l.llmsData,
    wikiUrl: w.wikiUrl,
    geoFindings: [...r.findings, ...l.findings, ...w.findings],
    geoScore: Math.round(((r.score/r.maxScore)+(l.score/l.maxScore)+(w.score/w.maxScore))/3*100),
  };
}
// CHECKLIST DATA — 23 tasks
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
// VENDASTA CITATION SOURCES (~40+ directories)
// ─────────────────────────────────────────────
const CITATION_SOURCES = [
  // Tier 1 — High authority, heavily weighted by AI
  {id:"cite_google",    name:"Google Business Profile", tier:1, url:"business.google.com"},
  {id:"cite_bing",      name:"Bing Places",             tier:1, url:"bingplaces.com"},
  {id:"cite_apple",     name:"Apple Maps",              tier:1, url:"mapsconnect.apple.com"},
  {id:"cite_yelp",      name:"Yelp",                    tier:1, url:"biz.yelp.com"},
  {id:"cite_facebook",  name:"Facebook Business",       tier:1, url:"facebook.com/business"},
  {id:"cite_bbb",       name:"Better Business Bureau",  tier:1, url:"bbb.org"},
  // Tier 2 — Data aggregators (feed downstream sources)
  {id:"cite_infogroup",   name:"Data Axle / Infogroup", tier:2, url:"dataaxle.com"},
  {id:"cite_localeze",    name:"Neustar Localeze",      tier:2, url:"neustarlocaleze.biz"},
  {id:"cite_acxiom",      name:"Acxiom",                tier:2, url:"acxiom.com"},
  {id:"cite_factual",     name:"Foursquare / Factual",  tier:2, url:"foursquare.com"},
  // Tier 3 — General directories
  {id:"cite_yellowpages", name:"YellowPages",           tier:3, url:"yellowpages.com"},
  {id:"cite_superpages",  name:"Superpages",            tier:3, url:"superpages.com"},
  {id:"cite_whitepages",  name:"Whitepages",            tier:3, url:"whitepages.com"},
  {id:"cite_manta",       name:"Manta",                 tier:3, url:"manta.com"},
  {id:"cite_mapquest",    name:"MapQuest",              tier:3, url:"mapquest.com"},
  {id:"cite_hotfrog",     name:"Hotfrog",               tier:3, url:"hotfrog.com"},
  {id:"cite_opendius",    name:"Opendi",                tier:3, url:"opendi.com"},
  {id:"cite_merchantcircle", name:"MerchantCircle",     tier:3, url:"merchantcircle.com"},
  {id:"cite_cylex",       name:"Cylex",                 tier:3, url:"cylex-usa.com"},
  {id:"cite_showmelocal", name:"ShowMeLocal",           tier:3, url:"showmelocal.com"},
  {id:"cite_n49",         name:"N49",                   tier:3, url:"n49.com"},
  {id:"cite_elocal",      name:"eLocal",                tier:3, url:"elocal.com"},
  {id:"cite_tupalo",      name:"Tupalo",                tier:3, url:"tupalo.com"},
  {id:"cite_expressupdate", name:"Express Update",      tier:3, url:"expressupdate.com"},
  {id:"cite_loc8nearme",  name:"Loc8NearMe",            tier:3, url:"loc8nearme.com"},
  {id:"cite_brownbook",   name:"Brownbook",             tier:3, url:"brownbook.net"},
  {id:"cite_citysearch",  name:"Citysearch",            tier:3, url:"citysearch.com"},
  {id:"cite_ibegin",      name:"iBegin",                tier:3, url:"ibegin.com"},
  {id:"cite_where",       name:"Where To?",             tier:3, url:"where.com"},
  {id:"cite_2findlocal",  name:"2FindLocal",            tier:3, url:"2findlocal.com"},
  // Tier 4 — Industry / niche
  {id:"cite_angieslist",  name:"Angi (Angie's List)",   tier:4, url:"angi.com"},
  {id:"cite_thumbtack",   name:"Thumbtack",             tier:4, url:"thumbtack.com"},
  {id:"cite_homeadvisor", name:"HomeAdvisor",           tier:4, url:"homeadvisor.com"},
  {id:"cite_houzz",       name:"Houzz",                 tier:4, url:"houzz.com"},
  {id:"cite_nextdoor",    name:"Nextdoor",              tier:4, url:"nextdoor.com"},
  {id:"cite_chamberofcommerce", name:"Chamber of Commerce", tier:4, url:"chamberofcommerce.com"},
  {id:"cite_local_com",   name:"Local.com",             tier:4, url:"local.com"},
  {id:"cite_yasabe",      name:"Yasabe (Hispanic)",     tier:4, url:"yasabe.com"},
  {id:"cite_opentable",   name:"OpenTable",             tier:4, url:"opentable.com"},
  {id:"cite_tripadvisor", name:"TripAdvisor",           tier:4, url:"tripadvisor.com"},
  {id:"cite_healthgrades", name:"Healthgrades",         tier:4, url:"healthgrades.com"},
  {id:"cite_vitals",      name:"Vitals",                tier:4, url:"vitals.com"},
  {id:"cite_lawyers_com", name:"Lawyers.com",           tier:4, url:"lawyers.com"},
];

const CITATION_TIER_LABELS = {1:"Authority",2:"Aggregator",3:"Directory",4:"Niche"};
const CITATION_TIER_COL = {1:T.blue,2:T.teal,3:T.gold,4:T.ghost};

// Deterministically generate citation status per client so it's stable
function getCitationStatus(clientId, sourceId){
  const seed = (clientId+sourceId).split("").reduce((a,c)=>a+c.charCodeAt(0),0);
  const r = (Math.sin(seed*9301+49297)*233280) % 1;
  const abs = r < 0 ? r+1 : r;
  if(abs < 0.30) return "missing";
  if(abs < 0.55) return "inaccurate";
  return "accurate";
}

const CHECKLIST_CATS = [
  { name:"Google Business Profile", items:[
    {id:"gbp_complete",task:"Complete GBP listing — all fields, services, photos",time:"45 min",price:150},
    {id:"gbp_post",task:"Monthly GBP posts published (4 posts)",time:"1 hr",price:200},
    {id:"gbp_qa",task:"Q&A entries added targeting AI search queries",time:"30 min",price:150},
    {id:"gbp_categories",task:"All relevant business categories added",time:"15 min",price:75},
  ]},
  { name:"Schema Markup", items:[
    {id:"schema_local",task:"LocalBusiness structured data implemented",time:"1 hr",price:200},
    {id:"schema_faq",task:"FAQ page with structured data markup",time:"45 min",price:150},
    {id:"schema_service",task:"Service schema for all offerings",time:"1 hr",price:200},
    {id:"schema_review",task:"Review/rating schema implemented",time:"30 min",price:150},
  ]},
  { name:"On-Page Content", items:[
    {id:"content_faq",task:"FAQ page targeting conversational AI queries",time:"2 hr",price:300},
    {id:"content_areas",task:"Service area pages for each city/neighborhood",time:"2 hr",price:250},
    {id:"content_h1",task:"Headers rewritten for voice and AI search",time:"1 hr",price:150},
    {id:"content_about",task:"About page optimized with geo-specific content",time:"1 hr",price:200},
  ]},
  { name:"Citation Consistency", items:[
    {id:"cite_audit",    task:"Full citation audit across 40+ directories",time:"2 hr",price:300},
    {id:"cite_sync",     task:"Vendasta Listings Sync activated — NAP pushed to all sources",time:"1 hr",price:200},
    {id:"cite_tier1",    task:"Tier 1 authority sources accurate (Google, Bing, Apple, Yelp, Facebook, BBB)",time:"45 min",price:150},
    {id:"cite_aggregators",task:"Data aggregators corrected (Infogroup, Localeze, Acxiom, Foursquare)",time:"1 hr",price:150},
    {id:"cite_duplicates",task:"Duplicate listings identified and suppressed",time:"30 min",price:75},
  ]},
  { name:"Reviews", items:[
    {id:"review_system",task:"Review request system set up",time:"1 hr",price:200},
    {id:"review_respond",task:"All existing reviews responded to",time:"30 min",price:100},
    {id:"review_monthly",task:"Monthly review monitoring active",time:"ongoing",price:150},
  ]},
  { name:"Technical GEO", items:[
    {id:"tech_llmstxt",task:"llms.txt file created for AI crawlers",time:"30 min",price:150},
    {id:"tech_sitemap",task:"Sitemap submitted to Google and Bing",time:"15 min",price:75},
    {id:"tech_speed",task:"Core Web Vitals audit completed",time:"2 hr",price:300},
  ]},
];
const TOTAL_TASKS = CHECKLIST_CATS.reduce((a,c)=>a+c.items.length,0);
const TOTAL_VALUE = CHECKLIST_CATS.reduce((a,c)=>a+c.items.reduce((b,i)=>b+i.price,0),0);

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
const gradeFromScore = s => s>=90?"A":s>=80?"B":s>=70?"C":s>=60?"D":"F";
const GRADE_COL = {A:T.teal,B:"#48BB78",C:T.gold,D:T.orange,F:T.red};
const gcol = g => GRADE_COL[g]||T.red;

// ─────────────────────────────────────────────
// GLOBAL STYLES
// ─────────────────────────────────────────────
const GS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:'Plus Jakarta Sans',system-ui,sans-serif;background:${T.bg};color:${T.text};}
::-webkit-scrollbar{width:6px;}
::-webkit-scrollbar-track{background:${T.input};}
::-webkit-scrollbar-thumb{background:${T.ghost};border-radius:3px;}
::-webkit-scrollbar-thumb:hover{background:#718096;}
@keyframes slideIn{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:translateY(0);}}
@keyframes checkPop{0%,100%{transform:scale(1)}50%{transform:scale(1.2)}}
@keyframes spin{to{transform:rotate(360deg)}}
.slide-in{animation:slideIn 0.25s ease-out forwards;}
.check-pop{animation:checkPop 0.2s ease-out;}
.spin{animation:spin 0.8s linear infinite;display:inline-block;}
.wavy-bg{background:linear-gradient(135deg,${T.blue} 0%,${T.cyan} 100%);position:relative;overflow:hidden;}
.wavy-bg::before{content:'';position:absolute;inset:0;background:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 320'%3E%3Cpath fill='rgba(255,255,255,0.08)' d='M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,128C960,128,1056,192,1152,208C1248,224,1344,192,1392,176L1440,160L1440,320L0,320Z'/%3E%3C/svg%3E") no-repeat bottom/cover;opacity:0.4;}
.dark-card{background:linear-gradient(127.09deg,rgba(6,11,40,0.94) 19.41%,rgba(10,14,35,0.49) 76.65%);}
`;

// ─────────────────────────────────────────────
// PRIMITIVES
// ─────────────────────────────────────────────
const Card = ({children,style={}}) => (
  <div style={{background:T.white,borderRadius:16,padding:24,boxShadow:"0 1px 4px rgba(0,0,0,0.06)",border:`1px solid ${T.border}`,...style}}>
    {children}
  </div>
);

const Inp = ({value,onChange,placeholder,style={}}) => (
  <input value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
    style={{width:"100%",background:T.input,border:`1px solid ${T.border}`,borderRadius:12,
      padding:"10px 14px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none",...style}}
    onFocus={e=>e.target.style.boxShadow=`0 0 0 2px ${T.blue}44`}
    onBlur={e=>e.target.style.boxShadow="none"} />
);

function Btn({children,onClick,variant="ghost",disabled,style={},sm}){
  const base={display:"inline-flex",alignItems:"center",gap:6,padding:sm?"6px 14px":"9px 18px",
    borderRadius:12,border:"none",cursor:disabled?"not-allowed":"pointer",fontSize:sm?12:13,
    fontWeight:700,fontFamily:"inherit",opacity:disabled?0.5:1,transition:"opacity 0.15s",whiteSpace:"nowrap"};
  const v={
    primary:{background:`linear-gradient(135deg,${T.blue},${T.cyan})`,color:T.white,boxShadow:`0 4px 12px ${T.blue}33`},
    ghost:{background:T.input,color:T.text},
    success:{background:`${T.teal}22`,color:T.teal},
    danger:{background:"#FEE2E2",color:T.red},
  };
  return <button onClick={disabled?undefined:onClick} style={{...base,...v[variant],...style}}>{children}</button>;
}

function ScoreRing({score,size=100,sw=8,platform,showGrade=false,animate=true}){
  const [disp,setDisp]=useState(animate?0:score);
  useEffect(()=>{if(animate){const t=setTimeout(()=>setDisp(score),80);return()=>clearTimeout(t);}},[score,animate]);
  const g=gradeFromScore(score),col=gcol(g),r=(size-sw)/2,circ=2*Math.PI*r,offset=circ-(disp/100)*circ;
  return(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
      <div style={{position:"relative",width:size,height:size}}>
        <svg width={size} height={size} style={{transform:"rotate(-90deg)"}}>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={sw}/>
          <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={col} strokeWidth={sw}
            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
            style={{transition:animate?"stroke-dashoffset 1s ease-out":"none"}}/>
        </svg>
        <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
          <span style={{fontSize:size*0.27,fontWeight:800,color:col,lineHeight:1}}>{Math.round(disp)}</span>
          {showGrade&&<span style={{fontSize:size*0.17,fontWeight:700,color:col}}>{g}</span>}
        </div>
      </div>
      {platform&&<span style={{fontSize:11,color:T.ghost,fontWeight:600,textAlign:"center"}}>{platform}</span>}
    </div>
  );
}

const GradeBadge = ({grade}) => {
  const col=gcol(grade);
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:36,height:36,borderRadius:10,background:`${col}22`,color:col,fontWeight:800,fontSize:14}}>{grade}</span>;
};

// ─────────────────────────────────────────────
// SIDEBAR
// ─────────────────────────────────────────────
function Sidebar({activeNav,onNavChange}){
  const nav=[{id:"clients",label:"Clients",icon:"◈"},{id:"schema",label:"Schema",icon:"⚡"},{id:"settings",label:"Settings",icon:"⚙"}];
  return(
    <div style={{width:260,height:"100%",background:T.white,display:"flex",flexDirection:"column",
      boxShadow:"2px 0 12px rgba(0,0,0,0.06)",flexShrink:0,borderRight:`1px solid ${T.border}`}}>
      <div style={{padding:"28px 28px 16px"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:34,height:34,borderRadius:10,background:`linear-gradient(135deg,${T.blue},${T.cyan})`,
            display:"flex",alignItems:"center",justifyContent:"center",boxShadow:`0 4px 12px ${T.blue}44`}}>
            <span style={{color:T.white,fontWeight:800,fontSize:16}}>S</span>
          </div>
          <div>
            <span style={{fontWeight:800,fontSize:18,color:T.text}}>SHARK</span>
            <span style={{fontWeight:800,fontSize:18,color:T.blue}}>AI</span>
          </div>
        </div>
        <div style={{fontSize:10,color:T.ghost,marginTop:4,letterSpacing:1.5,fontWeight:600}}>GEO INTELLIGENCE PLATFORM</div>
      </div>
      <div style={{margin:"0 20px",height:1,background:T.border}}/>
      <nav style={{flex:1,padding:"16px 12px"}}>
        {nav.map(item=>{
          const active=activeNav===item.id;
          return(
            <button key={item.id} onClick={()=>onNavChange(item.id)}
              style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:12,
                border:"none",cursor:"pointer",background:active?T.white:"transparent",
                boxShadow:active?"0 2px 8px rgba(0,0,0,0.08)":"none",marginBottom:2,transition:"all 0.15s",fontFamily:"inherit"}}>
              <div style={{width:34,height:34,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:16,background:active?`linear-gradient(135deg,${T.blue},${T.cyan})`:T.input,
                color:active?T.white:T.blue,boxShadow:active?`0 4px 10px ${T.blue}33`:"none"}}>
                {item.icon}
              </div>
              <span style={{fontSize:13,fontWeight:700,color:active?T.text:T.ghost}}>{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div style={{padding:"0 16px 16px"}}>
        <div style={{background:`linear-gradient(135deg,${T.blue},${T.cyan})`,borderRadius:16,padding:20,textAlign:"center"}}>
          <div style={{fontSize:22,marginBottom:6}}>🦈</div>
          <div style={{color:T.white,fontWeight:700,fontSize:13}}>Shark AI Docs</div>
          <div style={{color:"rgba(255,255,255,0.7)",fontSize:11,margin:"4px 0 12px"}}>F10 to show/hide window</div>
          <button style={{width:"100%",background:T.white,border:"none",borderRadius:10,padding:"8px 0",
            fontSize:12,fontWeight:700,color:T.text,cursor:"pointer",fontFamily:"inherit"}}>Documentation</button>
        </div>
      </div>
      <div style={{padding:"0 24px 20px",borderTop:`1px solid ${T.border}`,paddingTop:12}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12}}>
          <span style={{color:T.ghost}}>Intent Signals</span>
          <span style={{color:T.teal,fontWeight:700}}>1,247</span>
        </div>
        <div style={{fontSize:10,color:T.ghost,marginTop:2}}>v1.0.0</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SIGN-IN MODAL
// ─────────────────────────────────────────────
function SignInModal({onClose,onSignIn}){
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [err,setErr]=useState("");
  const submit=()=>{
    if(!email||!pass){setErr("Please enter email and password.");return;}
    onSignIn({email,name:email.split("@")[0]});
    onClose();
  };
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}}
      onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div style={{background:T.white,borderRadius:20,padding:36,width:380,boxShadow:"0 20px 60px rgba(0,0,0,0.3)"}}>
        <div style={{fontWeight:800,fontSize:20,color:T.text,marginBottom:4}}>Sign In</div>
        <div style={{fontSize:13,color:T.ghost,marginBottom:24}}>Access your Shark AI dashboard</div>
        {err&&<div style={{background:`${T.red}12`,border:`1px solid ${T.red}33`,borderRadius:8,padding:"8px 12px",fontSize:12,color:T.red,marginBottom:14}}>{err}</div>}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:6}}>EMAIL</div>
          <Inp value={email} onChange={setEmail} placeholder="you@sharkbranding.com"/>
        </div>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:6}}>PASSWORD</div>
          <Inp value={pass} onChange={setPass} placeholder="••••••••" style={{fontFamily:"inherit"}}/>
        </div>
        <div style={{display:"flex",gap:10}}>
          <Btn variant="primary" onClick={submit} style={{flex:1,justifyContent:"center"}}>Sign In</Btn>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        </div>
        <div style={{fontSize:11,color:T.ghost,textAlign:"center",marginTop:14}}>Demo: any email + password works</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HEADER
// ─────────────────────────────────────────────
function Header({title,breadcrumb,user,onSignIn,onSignOut}){
  const [showModal,setShowModal]=useState(false);
  return(
    <>
    <div className="wavy-bg" style={{padding:"20px 32px",flexShrink:0}}>
      <div style={{position:"relative",zIndex:1,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div>
          <div style={{color:"rgba(255,255,255,0.65)",fontSize:12,marginBottom:2}}>{breadcrumb}</div>
          <div style={{color:T.white,fontSize:22,fontWeight:800}}>{title}</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <input placeholder="Search clients..."
            style={{background:"rgba(255,255,255,0.2)",backdropFilter:"blur(8px)",border:"1px solid rgba(255,255,255,0.3)",
              borderRadius:24,padding:"8px 16px",color:T.white,fontSize:13,outline:"none",width:220,fontFamily:"inherit"}}/>
          {user?(
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <div style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:10,
                padding:"6px 12px",color:T.white,fontSize:12,fontWeight:600}}>
                👤 {user.name}
              </div>
              <button onClick={onSignOut} style={{background:"transparent",border:"1px solid rgba(255,255,255,0.3)",
                borderRadius:10,padding:"6px 12px",color:T.white,fontSize:12,cursor:"pointer",fontFamily:"inherit"}}>
                Sign Out
              </button>
            </div>
          ):(
            <button onClick={()=>setShowModal(true)}
              style={{background:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.3)",borderRadius:10,
                padding:"8px 14px",color:T.white,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"inherit"}}>
              👤 Sign In
            </button>
          )}
          <button style={{background:"transparent",border:"none",color:T.white,cursor:"pointer",fontSize:18}}>🔔</button>
        </div>
      </div>
    </div>
    {showModal&&<SignInModal onClose={()=>setShowModal(false)} onSignIn={onSignIn}/>}
    </>
  );
}

// ─────────────────────────────────────────────
// STAT CARD
// ─────────────────────────────────────────────
function StatCard({label,value,change,positive,icon}){
  return(
    <Card style={{padding:20}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between"}}>
        <div>
          <div style={{fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1.2,marginBottom:6}}>{label}</div>
          <div style={{fontSize:26,fontWeight:800,color:T.text}}>{value}</div>
          <div style={{display:"flex",alignItems:"center",gap:4,marginTop:6}}>
            <span style={{fontSize:11,fontWeight:700,color:positive?T.teal:T.red}}>{positive?"▲":"▼"} {change}</span>
            <span style={{fontSize:11,color:T.ghost}}>vs last month</span>
          </div>
        </div>
        <div style={{width:44,height:44,borderRadius:12,background:`linear-gradient(135deg,${T.blue},${T.cyan})`,
          display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,boxShadow:`0 4px 12px ${T.blue}33`}}>
          {icon}
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────
// CLIENT LIST
// ─────────────────────────────────────────────
function ClientList({clients=[],onSelect,onAdd,onDelete}){
  const [showForm,setShowForm]=useState(false);
  const [form,setForm]=useState({name:"",domain:"",businessType:"",city:"",gtm:""});
  const avg=clients.length?Math.round(clients.reduce((a,c)=>a+c.score,0)/clients.length):0;
  const aCount=clients.filter(c=>c.grade==="A").length;
  const submit=()=>{
    if(form.name&&form.domain&&form.businessType){onAdd(form);setForm({name:"",domain:"",businessType:"",gtm:"",city:""});setShowForm(false);}
  };
  return(
    <div style={{flex:1,padding:28,overflow:"auto"}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:28}}>
        <StatCard label="TOTAL CLIENTS" value={clients.length} change="+2" positive icon="👥"/>
        <StatCard label="AVG. GEO SCORE" value={avg} change="+3.5%" positive icon="📊"/>
        <StatCard label="A-GRADE CLIENTS" value={aCount} change="+1" positive icon="🏆"/>
      </div>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:18,fontWeight:800,color:T.text}}>All Clients</span>
          <span style={{background:T.input,color:T.ghost,fontSize:12,padding:"3px 10px",borderRadius:20}}>{clients.length} total</span>
        </div>
        <Btn variant="primary" onClick={()=>setShowForm(!showForm)}>+ Add Client</Btn>
      </div>
      <div style={{overflow:"hidden",maxHeight:showForm?200:0,opacity:showForm?1:0,transition:"all 0.3s ease",marginBottom:showForm?16:0}}>
        <Card>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr auto",gap:12,alignItems:"flex-end"}}>
            {[["Business Name","name"],["City","city"],["Domain","domain"],["Business Type","businessType"],["GTM Container ID","gtm"]].map(([lbl,key])=>(
              <div key={key}>
                <div style={{fontSize:11,fontWeight:700,color:T.ghost,marginBottom:6,letterSpacing:1}}>{lbl.toUpperCase()}</div>
                <Inp value={form[key]} onChange={v=>setForm({...form,[key]:v})} placeholder={lbl}/>
              </div>
            ))}
            <Btn variant="primary" onClick={submit}>Save</Btn>
          </div>
        </Card>
      </div>
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {clients.map((c,i)=>(
          <div key={c.id} className="slide-in"
            style={{animationDelay:`${i*40}ms`,background:T.white,borderRadius:16,padding:"16px 20px",
              cursor:"pointer",border:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:16,transition:"all 0.15s"}}
            onClick={()=>onSelect(c)}
            onMouseEnter={e=>{e.currentTarget.style.boxShadow=`0 4px 16px ${T.blue}1a`;e.currentTarget.style.borderColor=T.blue;}}
            onMouseLeave={e=>{e.currentTarget.style.boxShadow="none";e.currentTarget.style.borderColor=T.border;}}>
            <ScoreRing score={c.score} size={54} sw={5}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:15,color:T.text}}>{c.name}</div>
              <div style={{fontSize:12,color:T.ghost,marginTop:2}}>{c.domain} · {c.businessType}{c.city?` · ${c.city}`:""}</div>
            </div>
            <GradeBadge grade={c.grade}/>
            <span style={{fontSize:12,color:T.ghost,minWidth:90,textAlign:"right"}}>{c.lastUpdated}</span>
            <button onClick={e=>{e.stopPropagation();onDelete(c.id);}}
              style={{background:"transparent",border:"none",cursor:"pointer",padding:6,color:T.ghost,borderRadius:8,fontSize:14,transition:"all 0.15s"}}
              onMouseEnter={e=>{e.currentTarget.style.background="#FEE2E2";e.currentTarget.style.color=T.red;}}
              onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color=T.ghost;}}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// OVERVIEW TAB
// ─────────────────────────────────────────────

// Generate seeded-random 6-month citation trend for a client
// Uses client.id as seed so it's stable across renders
function seededRand(seed,i){let x=Math.sin(seed*9301+(i+1)*49297)*233280;return x-Math.floor(x);}
function genTrend(clientId,base,variance,months){
  const seed=clientId.split("").reduce((a,c)=>a+c.charCodeAt(0),0);
  let val=base;
  return months.map((m,i)=>{
    const delta=(seededRand(seed,i)-0.42)*variance;
    val=Math.max(5,Math.min(99,Math.round(val+delta)));
    return{m,v:val};
  });
}

const LAST_6=()=>{
  const months=[];
  for(let i=5;i>=0;i--){
    const d=new Date();d.setMonth(d.getMonth()-i);
    months.push(d.toLocaleString("default",{month:"short"}));
  }
  return months;
};

const ACTIONS=[
  {n:"01",title:"Add LocalBusiness schema markup",detail:"Missing structured data for local search",effort:"Low"},
  {n:"02",title:"Optimize Google Business Profile",detail:"Update hours and add more photos",effort:"Medium"},
  {n:"03",title:"Fix NAP consistency issues",detail:"Address mismatch in 3 citation sources",effort:"High"},
  {n:"04",title:"Respond to pending reviews",detail:"5 reviews awaiting response on Google",effort:"Low"},
];
const EFFORT_COL={Low:`${T.teal}22|${T.teal}`,Medium:`${T.gold}22|${T.gold}`,High:`${T.orange}22|${T.orange}`};

// SVG line chart helper
function buildPath(data,W=740,H=160,ox=30,oy=10){
  const max=Math.max(...data.map(d=>d.v),1);
  const pts=data.map((d,i)=>{
    const x=(i/(data.length-1))*W+ox;
    const y=H+oy-(d.v/max)*H;
    return[x,y];
  });
  const line=pts.map((p,i)=>`${i===0?"M":"L"}${p[0]},${p[1]}`).join(" ");
  const area=`${line} L${pts[pts.length-1][0]},${H+oy} L${ox},${H+oy} Z`;
  return{pts,line,area,max};
}

function CitationTrendChart({clientId,score}){
  const months=LAST_6();
  const base=score||60;
  const series=[
    {key:"google", label:"Google AI",  col:"#4318FF", data:genTrend(clientId+"g",base,18,months)},
    {key:"bing",   label:"Bing",       col:"#0075FF", data:genTrend(clientId+"b",base-10,16,months)},
    {key:"apple",  label:"Apple Siri", col:"#01B574", data:genTrend(clientId+"a",base-16,14,months)},
  ];
  const [hover,setHover]=useState(null);
  const allVals=series.flatMap(s=>s.data.map(d=>d.v));
  const globalMax=Math.max(...allVals,1);
  const W=740,H=150,ox=30,oy=10;

  // Latest values for legend
  const latest=series.map(s=>({...s,cur:s.data[s.data.length-1].v,prev:s.data[s.data.length-2].v}));

  return(
    <div className="dark-card" style={{borderRadius:20,padding:28}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{color:"rgba(255,255,255,0.75)",fontSize:14,fontWeight:700}}>AI Citation Trend</div>
          <div style={{color:"rgba(255,255,255,0.4)",fontSize:11,marginTop:2}}>6-month lookback across platforms</div>
        </div>
        {/* Legend */}
        <div style={{display:"flex",gap:16}}>
          {latest.map(s=>{
            const delta=s.cur-s.prev;
            return(
              <div key={s.key} style={{textAlign:"right"}}>
                <div style={{display:"flex",alignItems:"center",gap:5,justifyContent:"flex-end",marginBottom:2}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:s.col}}/>
                  <span style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:600}}>{s.label}</span>
                </div>
                <div style={{fontSize:18,fontWeight:800,color:T.white}}>{s.cur}</div>
                <div style={{fontSize:11,fontWeight:700,color:delta>=0?T.teal:T.red}}>
                  {delta>=0?"▲":"▼"}{Math.abs(delta)} pts
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Chart */}
      <div style={{position:"relative"}}>
        <svg width="100%" viewBox={`0 0 ${W+ox+20} ${H+oy+20}`} preserveAspectRatio="none" style={{display:"block",overflow:"visible"}}>
          <defs>
            {series.map(s=>(
              <linearGradient key={s.key} id={`ag_${s.key}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={s.col} stopOpacity="0.25"/>
                <stop offset="100%" stopColor={s.col} stopOpacity="0"/>
              </linearGradient>
            ))}
          </defs>
          {/* Grid lines */}
          {[0,25,50,75,100].map(v=>(
            <line key={v} x1={ox} x2={W+ox}
              y1={H+oy-(v/100)*H} y2={H+oy-(v/100)*H}
              stroke="rgba(255,255,255,0.05)" strokeWidth="1" strokeDasharray="4,4"/>
          ))}
          {/* Y labels */}
          {[0,25,50,75,100].map(v=>(
            <text key={v} x={ox-6} y={H+oy-(v/100)*H+4} textAnchor="end"
              fill="rgba(255,255,255,0.25)" fontSize="9">{v}</text>
          ))}
          {/* Area fills (bottom series first) */}
          {[...series].reverse().map(s=>{
            const pts=s.data.map((d,i)=>[
              (i/(s.data.length-1))*W+ox,
              H+oy-(d.v/globalMax)*H
            ]);
            const line=pts.map((p,i)=>`${i===0?"M":"L"}${p[0]},${p[1]}`).join(" ");
            const area=`${line} L${pts[pts.length-1][0]},${H+oy} L${ox},${H+oy} Z`;
            return <path key={s.key+"area"} d={area} fill={`url(#ag_${s.key})`}/>;
          })}
          {/* Lines */}
          {series.map(s=>{
            const pts=s.data.map((d,i)=>[
              (i/(s.data.length-1))*W+ox,
              H+oy-(d.v/globalMax)*H
            ]);
            const line=pts.map((p,i)=>`${i===0?"M":"L"}${p[0]},${p[1]}`).join(" ");
            return <path key={s.key+"line"} d={line} fill="none" stroke={s.col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>;
          })}
          {/* Dots + hover */}
          {series.map(s=>s.data.map((d,i)=>{
            const x=(i/(s.data.length-1))*W+ox;
            const y=H+oy-(d.v/globalMax)*H;
            const isHov=hover?.i===i;
            return(
              <circle key={s.key+i} cx={x} cy={y} r={isHov?5:3}
                fill={T.white} stroke={s.col} strokeWidth="1.5"
                style={{cursor:"pointer",transition:"r 0.1s"}}
                onMouseEnter={()=>setHover({i,m:d.m})}
                onMouseLeave={()=>setHover(null)}/>
            );
          }))}
          {/* Hover vertical line */}
          {hover&&(()=>{
            const x=(hover.i/(months.length-1))*W+ox;
            return <line x1={x} x2={x} y1={oy} y2={H+oy} stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="3,3"/>;
          })()}
        </svg>

        {/* Hover tooltip */}
        {hover&&(
          <div style={{position:"absolute",top:0,left:`${(hover.i/(months.length-1))*92+4}%`,
            transform:"translateX(-50%)",background:"rgba(13,21,40,0.95)",
            border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,padding:"8px 12px",
            pointerEvents:"none",minWidth:120,zIndex:10}}>
            <div style={{fontSize:11,color:"rgba(255,255,255,0.5)",fontWeight:700,marginBottom:4}}>{hover.m}</div>
            {series.map(s=>(
              <div key={s.key} style={{display:"flex",justifyContent:"space-between",gap:12,fontSize:12}}>
                <span style={{color:s.col,fontWeight:600}}>{s.label}</span>
                <span style={{color:T.white,fontWeight:700}}>{s.data[hover.i].v}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* X axis */}
      <div style={{display:"flex",paddingLeft:30,paddingRight:20,marginTop:6}}>
        {months.map((m,i)=>(
          <span key={m} style={{flex:1,textAlign:"center",fontSize:10,
            color:hover?.i===i?"rgba(255,255,255,0.7)":"rgba(255,255,255,0.3)",
            fontWeight:hover?.i===i?700:400,transition:"color 0.1s"}}>{m}</span>
        ))}
      </div>
    </div>
  );
}

function GBPCard({client, gbmData, setGbmData, syncing, onSync}){
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: client.name,
    address: client.city||"",
    phone: "",
    claimed: "",
    verified: "",
    rating: "",
    reviews: "",
  });

  // Pre-fill form from existing gbmData if present
  useEffect(()=>{
    if(gbmData && gbmData.status==="verified"){
      setForm({
        name: gbmData.name||client.name,
        address: gbmData.address||"",
        phone: gbmData.phone||"",
        claimed: "yes",
        verified: "yes",
        rating: gbmData.rating||"",
        reviews: gbmData.reviews||"",
      });
    }
  },[gbmData]);

  const save=()=>{
    const claimed = form.claimed==="yes";
    const verified = form.verified==="yes";
    setGbmData({
      name: form.name||client.name,
      address: form.address,
      phone: form.phone,
      claimed,
      verified,
      gbpClaimed: claimed,
      rating: form.rating||null,
      reviews: form.reviews||null,
      category: client.businessType,
      status: verified?"verified": claimed?"unverified_listing":"no_gbp",
    });
    setEditing(false);
  };

  const Field=({label,k,placeholder,type="text"})=>(
    <div>
      <div style={{fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:4}}>{label}</div>
      <Inp value={form[k]} onChange={v=>setForm({...form,[k]:v})} placeholder={placeholder}/>
    </div>
  );

  const SelectField=({label,k,options})=>(
    <div>
      <div style={{fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1,marginBottom:4}}>{label}</div>
      <select value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})}
        style={{width:"100%",background:T.input,border:`1px solid ${T.border}`,borderRadius:12,
          padding:"10px 14px",fontSize:13,color:form[k]?T.text:T.ghost,fontFamily:"inherit",outline:"none"}}>
        <option value="">Select…</option>
        {options.map(([v,l])=><option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );

  // Show entry form (either first time or editing)
  if(!gbmData || editing){
    return(
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <span style={{fontWeight:800,color:T.text}}>Google Business Profile</span>
          {editing&&<Btn sm variant="ghost" onClick={()=>setEditing(false)}>Cancel</Btn>}
        </div>
        <div style={{fontSize:12,color:T.ghost,marginBottom:16,lineHeight:1.5}}>
          Enter the actual GBP details for this client. This isn't pulled automatically — you know the real status.
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <Field label="BUSINESS NAME" k="name" placeholder={client.name}/>
          <Field label="GTM CONTAINER ID" k="gtm" placeholder="GTM-XXXXXXX"/>
          <Field label="FULL ADDRESS (as it appears on GBP)" k="address" placeholder="123 Main St, Wesley Chapel, FL 33543"/>
          <Field label="PHONE" k="phone" placeholder="(813) 555-0000"/>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <SelectField label="GBP CLAIMED?" k="claimed" options={[["yes","✓ Yes — claimed"],["no","✗ No — unclaimed"]]}/>
            <SelectField label="VERIFIED BY GOOGLE?" k="verified" options={[["yes","✓ Yes — verified"],["no","✗ No — not verified"]]}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Field label="STAR RATING (optional)" k="rating" placeholder="4.8"/>
            <Field label="REVIEW COUNT (optional)" k="reviews" placeholder="47"/>
          </div>
          <Btn variant="primary" onClick={save}>Save GBP Data</Btn>
        </div>
      </Card>
    );
  }

  // Verified listing display
  const st=gbmData.status;
  const isClaimed=gbmData.claimed||gbmData.gbpClaimed;
  const isVerified=gbmData.verified||st==="verified";

  return(
    <Card>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{fontWeight:800,color:T.text}}>Google Business Profile</span>
        <Btn sm variant="ghost" onClick={()=>setEditing(true)}>✎ Edit</Btn>
      </div>

      {/* Status banner */}
      {isVerified?(
        <div style={{background:`${T.teal}12`,border:`1px solid ${T.teal}44`,borderRadius:10,
          padding:"10px 14px",marginBottom:14,fontSize:12,color:T.teal,fontWeight:600}}>
          ✓ Claimed &amp; Verified — GBP is active and controlled
        </div>
      ):isClaimed?(
        <div style={{background:`${T.gold}12`,border:`1px solid ${T.gold}44`,borderRadius:10,
          padding:"10px 14px",marginBottom:14,fontSize:12,color:T.gold,fontWeight:600}}>
          ⚠ Claimed but not yet verified by Google
        </div>
      ):(
        <div style={{background:`${T.red}10`,border:`1px solid ${T.red}33`,borderRadius:10,
          padding:"10px 14px",marginBottom:14,fontSize:12,color:T.red,fontWeight:600}}>
          ✗ Not claimed — listing may show wrong info in AI and Maps
        </div>
      )}

      {[
        ["Business Name", gbmData.name||client.name],
        ["Address", gbmData.address||"—"],
        ["Phone", gbmData.phone||"—"],
        ["Claimed", {val:isClaimed?"✓ Yes":"✗ No", col:isClaimed?T.teal:T.red}],
        ["Verified", {val:isVerified?"✓ Yes":"✗ No", col:isVerified?T.teal:T.red}],
        ...(gbmData.rating?[["Rating",{val:`${gbmData.rating}★ (${gbmData.reviews||"?"} reviews)`,col:T.gold}]]:[]),
      ].map(([k,v])=>(
        <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${T.border}`}}>
          <span style={{fontSize:12,color:T.ghost}}>{k}</span>
          <span style={{fontSize:12,fontWeight:600,color:v?.col||T.text}}>{v?.val||v}</span>
        </div>
      ))}
    </Card>
  );
}

function OverviewTab({client, gbmData, setGbmData, syncing, onSync, onTabSwitch}){
  const plats=[
    {label:"Google AI",score:Math.min(99,Math.max(20,client.score+Math.round(seededRand(client.id.charCodeAt(0)||1,0)*6-2)))},
    {label:"Bing",score:Math.min(99,Math.max(20,client.score-8+Math.round(seededRand(client.id.charCodeAt(0)||1,1)*4)))},
    {label:"Apple",score:Math.min(99,Math.max(20,client.score-14+Math.round(seededRand(client.id.charCodeAt(0)||1,2)*4)))},
  ];
  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <CitationTrendChart clientId={client.id} score={client.score}/>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20}}>
        <GBPCard client={client} gbmData={gbmData} setGbmData={setGbmData} syncing={syncing} onSync={onSync}/>
        <Card>
          <div style={{fontWeight:800,color:T.text,marginBottom:20}}>Platform Scores</div>
          <div style={{display:"flex",justifyContent:"space-around",alignItems:"center",paddingTop:8}}>
            {plats.map(p=><ScoreRing key={p.label} score={p.score} size={80} sw={7} platform={p.label}/>)}
          </div>
        </Card>
      </div>
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
          <span style={{fontWeight:800,color:T.text}}>Top Action Items</span>
          <Btn sm variant="primary" onClick={()=>onTabSwitch?.("CHECKLIST")}>View Checklist →</Btn>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          {ACTIONS.map(a=>{const[bg,col]=EFFORT_COL[a.effort].split("|");return(
            <div key={a.n} style={{display:"flex",alignItems:"flex-start",gap:12}}>
              <span style={{fontWeight:800,color:T.blue,fontSize:13,width:24,flexShrink:0}}>{a.n}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:T.text}}>{a.title}</div>
                <div style={{fontSize:11,color:T.ghost,marginTop:2}}>{a.detail}</div>
              </div>
              <span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:bg,color:col,fontWeight:700,whiteSpace:"nowrap"}}>{a.effort}</span>
            </div>
          );})}
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────
// AUDIT TAB
// ─────────────────────────────────────────────
// Each signal has a finding for each possible status so they always match
const AUDIT_DATA=[
  {name:"Google AI Overview",signals:[
    {signal:"LocalBusiness Schema",impact:"High",findings:{
      Pass:"LocalBusiness schema present and valid",
      Warning:"Schema found but missing recommended fields (hours, priceRange)",
      Fail:"No LocalBusiness schema markup detected",
    }},
    {signal:"NAP Consistency",impact:"Medium",findings:{
      Pass:"Name, address, and phone match across all sources",
      Warning:"Minor formatting differences detected across listings",
      Fail:"NAP mismatch found — address or phone conflicts with Google",
    }},
    {signal:"Review Signals",impact:"Low",findings:{
      Pass:"Strong review presence with recent activity",
      Warning:"Reviews present but no owner responses found",
      Fail:"Fewer than 10 reviews or no recent review activity",
    }},
    {signal:"Content Freshness",impact:"Medium",findings:{
      Pass:"GBP posts and website updated within last 30 days",
      Warning:"Last update was 30–90 days ago",
      Fail:"No content updates detected in 90+ days",
    }},
    {signal:"FAQ Schema",impact:"High",findings:{
      Pass:"FAQ schema present and indexed by Google",
      Warning:"FAQ content exists but schema markup is missing",
      Fail:"No FAQ schema or FAQ content found",
    }},
    {signal:"Service Categories",impact:"Medium",findings:{
      Pass:"Primary and secondary categories fully populated",
      Warning:"Primary category set but missing secondary categories",
      Fail:"Business category not set or incorrect",
    }},
  ]},
  {name:"Bing Copilot",signals:[
    {signal:"Bing Places Listing",impact:"Medium",findings:{
      Pass:"Verified and active listing on Bing Places",
      Warning:"Listing exists but not claimed or verified",
      Fail:"No Bing Places listing found",
    }},
    {signal:"Category Alignment",impact:"Low",findings:{
      Pass:"Categories match Google listing exactly",
      Warning:"Category mismatch between Bing and Google",
      Fail:"Incorrect or missing category on Bing",
    }},
    {signal:"Photo Quality",impact:"Medium",findings:{
      Pass:"High-quality photos present on listing",
      Warning:"Photos present but low resolution or outdated",
      Fail:"No photos on Bing listing",
    }},
    {signal:"Business Description",impact:"Low",findings:{
      Pass:"Description is complete and keyword-rich (250+ chars)",
      Warning:"Description present but under 250 characters",
      Fail:"No business description on Bing listing",
    }},
  ]},
  {name:"Apple Siri / Spotlight",signals:[
    {signal:"Apple Maps Listing",impact:"High",findings:{
      Pass:"Listing verified and accurate on Apple Maps",
      Warning:"Listing found but unverified or unclaimed",
      Fail:"Business not found on Apple Maps",
    }},
    {signal:"Business Hours",impact:"Medium",findings:{
      Pass:"Hours are accurate and synced with Apple Maps",
      Warning:"Hours present but may be outdated",
      Fail:"No business hours set on Apple Maps",
    }},
    {signal:"Payment Methods",impact:"Low",findings:{
      Pass:"Payment methods fully listed",
      Warning:"Some payment methods missing",
      Fail:"No payment information on listing",
    }},
    {signal:"Website Link",impact:"Low",findings:{
      Pass:"Website correctly linked and resolving",
      Warning:"Website linked but redirects or loads slowly",
      Fail:"No website link on Apple Maps listing",
    }},
  ]},
  {name:"Citation Consistency",isCitations:true,signals:[
    // signals here are generated dynamically per-client in AuditTab
  ]},
  {name:"AI Mentions & Entity Coverage",signals:[
    {signal:"Brand Entity Recognition",impact:"High",findings:{
      Pass:"Business recognized as a named entity in AI results",
      Warning:"Business appears in AI results but not as a confirmed entity",
      Fail:"Business not recognized as a named entity in AI results",
    }},
    {signal:"Knowledge Panel",impact:"High",findings:{
      Pass:"Google Knowledge Panel exists for this business",
      Warning:"Knowledge Panel present but showing incomplete data",
      Fail:"No Google Knowledge Panel found — GBP likely unclaimed",
    }},
    {signal:"Wikipedia / Wikidata",impact:"Medium",findings:{
      Pass:"Wikidata entity exists and linked to Google",
      Warning:"Partial Wikidata entry found — needs completion",
      Fail:"No Wikidata entity found for this business",
    }},
    {signal:"llms.txt Present",impact:"Medium",findings:{
      Pass:"llms.txt found at domain root and properly formatted",
      Warning:"llms.txt exists but missing key sections",
      Fail:"No llms.txt file found at domain root",
    }},
    {signal:"AI-Cited Content",impact:"High",findings:{
      Pass:"Pages from this domain appear in AI overview citations",
      Warning:"Limited AI citation presence — 1–2 pages only",
      Fail:"No pages from this domain found in AI overview citations",
    }},
    {signal:"Structured Q&A Coverage",impact:"High",findings:{
      Pass:"FAQ and Q&A content indexed and appearing in AI answers",
      Warning:"Q&A content exists but not structured for AI parsing",
      Fail:"No FAQ or Q&A content found on site",
    }},
  ]},
];
const SSTYLE={Pass:{dot:T.teal,text:T.teal,bg:`${T.teal}18`},Fail:{dot:T.red,text:T.red,bg:`${T.red}18`},Warning:{dot:T.gold,text:T.gold,bg:`${T.gold}18`}};

const CSTYLE={
  accurate:{dot:T.teal,text:T.teal,bg:`${T.teal}18`,label:"Accurate"},
  inaccurate:{dot:T.gold,text:T.gold,bg:`${T.gold}18`,label:"Inaccurate"},
  missing:{dot:T.red,text:T.red,bg:`${T.red}18`,label:"Missing"},
};

function CitationGrid({clientId}){
  const [filter,setFilter]=useState("all");
  const sources=CITATION_SOURCES.map(s=>({...s,status:getCitationStatus(clientId,s.id)}));
  const filtered=filter==="all"?sources:sources.filter(s=>s.status===filter);
  const counts={
    accurate:sources.filter(s=>s.status==="accurate").length,
    inaccurate:sources.filter(s=>s.status==="inaccurate").length,
    missing:sources.filter(s=>s.status==="missing").length,
  };
  const pct=Math.round((counts.accurate/sources.length)*100);
  return(
    <div>
      {/* Summary bar */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
        {[
          {label:"Total Sources",val:sources.length,col:T.text},
          {label:"Accurate",val:counts.accurate,col:T.teal},
          {label:"Inaccurate",val:counts.inaccurate,col:T.gold},
          {label:"Missing",val:counts.missing,col:T.red},
        ].map(({label,val,col})=>(
          <div key={label} style={{textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:col}}>{val}</div>
            <div style={{fontSize:11,color:T.ghost,fontWeight:600}}>{label}</div>
          </div>
        ))}
      </div>
      {/* Score bar */}
      <div style={{padding:"12px 24px",borderBottom:`1px solid ${T.border}`}}>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,fontWeight:600,marginBottom:6}}>
          <span style={{color:T.ghost}}>Citation Accuracy</span>
          <span style={{color:pct>=70?T.teal:pct>=40?T.gold:T.red}}>{pct}%</span>
        </div>
        <div style={{height:6,background:T.border,borderRadius:4,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${pct}%`,borderRadius:4,transition:"width 0.5s",
            background:pct>=70?`linear-gradient(90deg,${T.teal},#48BB78)`:pct>=40?`linear-gradient(90deg,${T.gold},${T.orange})`:`linear-gradient(90deg,${T.red},${T.orange})`}}/>
        </div>
      </div>
      {/* Filter tabs */}
      <div style={{display:"flex",gap:0,borderBottom:`1px solid ${T.border}`}}>
        {[["all","All"],["accurate","Accurate"],["inaccurate","Inaccurate"],["missing","Missing"]].map(([k,lbl])=>(
          <button key={k} onClick={()=>setFilter(k)}
            style={{flex:1,padding:"9px 0",border:"none",background:"transparent",cursor:"pointer",
              fontSize:11,fontWeight:700,letterSpacing:0.8,fontFamily:"inherit",
              color:filter===k?T.blue:T.ghost,
              borderBottom:filter===k?`2px solid ${T.blue}`:"2px solid transparent"}}>
            {lbl}{k!=="all"?` (${counts[k]||0})`:""}
          </button>
        ))}
      </div>
      {/* Source rows */}
      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
        <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
          {["SOURCE","TIER","STATUS","ISSUE"].map(h=>(
            <th key={h} style={{textAlign:"left",padding:"8px 24px",fontSize:10,fontWeight:700,color:T.ghost,letterSpacing:1}}>{h}</th>
          ))}
        </tr></thead>
        <tbody>
          {filtered.map(s=>{
            const st=CSTYLE[s.status];
            const issue=s.status==="missing"
              ?`Not found on ${s.name} — listing needs to be created`
              :s.status==="inaccurate"
              ?`NAP data on ${s.name} doesn't match primary listing`
              :`Matches primary listing`;
            return(
              <tr key={s.id} style={{borderBottom:`1px solid ${T.border}`}}
                onMouseEnter={e=>e.currentTarget.style.background=T.input}
                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                <td style={{padding:"10px 24px",fontWeight:600,color:T.text}}>
                  <a href={`https://${s.url}`} target="_blank" rel="noreferrer"
                    style={{color:T.text,textDecoration:"none"}}
                    onMouseEnter={e=>e.target.style.color=T.blue}
                    onMouseLeave={e=>e.target.style.color=T.text}>
                    {s.name}
                  </a>
                </td>
                <td style={{padding:"10px 24px"}}>
                  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:6,
                    background:`${CITATION_TIER_COL[s.tier]}18`,color:CITATION_TIER_COL[s.tier]}}>
                    T{s.tier} {CITATION_TIER_LABELS[s.tier]}
                  </span>
                </td>
                <td style={{padding:"10px 24px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{width:6,height:6,borderRadius:"50%",background:st.dot}}/>
                    <span style={{color:st.text,fontWeight:700}}>{st.label}</span>
                  </div>
                </td>
                <td style={{padding:"10px 24px",color:T.ghost,fontSize:11}}>{issue}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab({auditResults,auditing,clientId,geoData,clientGtm}){
  const data = auditResults || AUDIT_DATA.map(plat=>({
    ...plat,
    signals:plat.isCitations?[]:plat.signals.map(s=>({
      signal:s.signal,
      impact:s.impact,
      status:"Fail",
      finding:s.findings.Fail,
    }))
  }));
  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {auditing&&(
        <div style={{background:`${T.blue}10`,border:`1px solid ${T.blue}33`,borderRadius:12,padding:"12px 18px",
          display:"flex",alignItems:"center",gap:10,fontSize:13,color:T.blue,fontWeight:600}}>
          <span className="spin" style={{width:14,height:14,border:`2px solid ${T.blue}33`,borderTopColor:T.blue,borderRadius:"50%",flexShrink:0}}/>
          Scanning citations, AI mentions, GBP signals and platform listings…
        </div>
      )}
      {auditResults&&!auditing&&(
        <div style={{background:`${T.teal}12`,border:`1px solid ${T.teal}44`,borderRadius:12,padding:"10px 18px",
          fontSize:13,color:T.teal,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
          ✓ Audit complete — {new Date().toLocaleTimeString()}
        </div>
      )}
      {data.map(plat=>(
        <Card key={plat.name} style={{padding:0,overflow:"hidden"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
            <span style={{fontWeight:800,color:T.text}}>{plat.name}</span>
          </div>
          {plat.isCitations
            ? <CitationGrid clientId={clientId}/>
            : (
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead><tr style={{borderBottom:`1px solid ${T.border}`}}>
                  {["SIGNAL","STATUS","FINDING","IMPACT"].map(h=><th key={h} style={{textAlign:"left",padding:"10px 24px",fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1.1}}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {plat.signals.map((s,i)=>{const st=SSTYLE[s.status];return(
                    <tr key={i} style={{borderBottom:`1px solid ${T.border}`}}
                      onMouseEnter={e=>e.currentTarget.style.background=T.input}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <td style={{padding:"12px 24px",fontWeight:600,color:T.text}}>{s.signal}</td>
                      <td style={{padding:"12px 24px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:7}}>
                          <div style={{width:7,height:7,borderRadius:"50%",background:st.dot}}/>
                          <span style={{color:st.text,fontWeight:700}}>{s.status}</span>
                        </div>
                      </td>
                      <td style={{padding:"12px 24px",color:T.ghost}}>{s.finding}</td>
                      <td style={{padding:"12px 24px"}}>
                        <span style={{fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:8,background:st.bg,color:st.text}}>{s.impact}</span>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            )
          }
        </Card>
      ))}

      {/* â”€â”€ Crawler Access Section â”€â”€ */}
      {(geoData?.crawlerMap?.length>0)&&(
        <div style={{background:T.white,borderRadius:16,border:`1px solid ${T.border}`,overflow:"hidden"}}>
          <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontWeight:800,color:T.text}}>AI Crawler Access</span>
            <span style={{fontSize:11,color:T.ghost}}>robots.txt analysis</span>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:10,padding:20}}>
            {geoData.crawlerMap.map(c=>(
              <div key={c.name} style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${T.border}`,
                background:c.status==="Blocked"?`${T.red}08`:c.status==="Allowed"?`${T.teal}08`:`${T.gold}08`}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:4}}>
                  <div style={{width:7,height:7,borderRadius:"50%",flexShrink:0,
                    background:c.status==="Blocked"?T.red:c.status==="Allowed"?T.teal:T.gold}}/>
                  <span style={{fontSize:12,fontWeight:700,color:T.text}}>{c.name}</span>
                </div>
                <div style={{fontSize:10,color:T.ghost}}>{c.owner}</div>
                <div style={{fontSize:10,fontWeight:600,marginTop:3,
                  color:c.status==="Blocked"?T.red:c.status==="Allowed"?T.teal:T.gold}}>{c.status}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* â”€â”€ llms.txt Section â”€â”€ */}
      {geoData&&(
        <div style={{background:T.white,borderRadius:16,border:`1px solid ${T.border}`,overflow:"hidden"}}>
          <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span style={{fontWeight:800,color:T.text}}>llms.txt â€” AI Readability</span>
            {geoData.llmsData&&<span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.teal}18`,color:T.teal,fontWeight:700}}>FOUND</span>}
            {!geoData.llmsData&&<span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.red}18`,color:T.red,fontWeight:700}}>MISSING</span>}
          </div>
          <div style={{padding:20}}>
            {geoData.llmsData&&(
              <pre style={{fontSize:11,color:T.text,background:T.input,padding:14,borderRadius:10,overflow:"auto",maxHeight:180,margin:0}}>{geoData.llmsData.substring(0,600)}{geoData.llmsData.length>600?"...":""}</pre>
            )}
            {geoData.llmsGenerated&&(
              <div>
                <div style={{fontSize:12,color:T.ghost,marginBottom:10}}>No llms.txt detected. Here is a starter file for this client:</div>
                <pre style={{fontSize:11,color:T.text,background:T.input,padding:14,borderRadius:10,overflow:"auto",maxHeight:200,margin:0}}>{geoData.llmsGenerated}</pre>
                <button onClick={()=>navigator.clipboard.writeText(geoData.llmsGenerated)}
                  style={{marginTop:10,fontSize:12,padding:"6px 16px",borderRadius:8,border:`1px solid ${T.blue}`,
                    background:"transparent",color:T.blue,cursor:"pointer",fontWeight:600}}>Copy llms.txt</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* â”€â”€ GTM Status Section â”€â”€ */}
      <div style={{background:T.white,borderRadius:16,border:`1px solid ${T.border}`,overflow:"hidden"}}>
        <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontWeight:800,color:T.text}}>Schema Injection â€” GTM</span>
          {clientGtm
            ?<span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.teal}18`,color:T.teal,fontWeight:700}}>{clientGtm}</span>
            :<span style={{fontSize:11,padding:"3px 10px",borderRadius:8,background:`${T.gold}18`,color:T.gold,fontWeight:700}}>NO GTM SET</span>}
        </div>
        <div style={{padding:"16px 24px",fontSize:13,color:T.ghost}}>
          {clientGtm
            ?"GTM container configured. Schema injection will deploy LocalBusiness, FAQ, and Service markup via this container on the next scheduled injection."
            :"Add a GTM Container ID to this client to enable monthly schema injection. Edit the client and enter the GTM-XXXXXXX container ID."}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// CHECKLIST TAB
// ─────────────────────────────────────────────
function deriveAutoChecked(clientId, auditResults, gbmData){
  const auto={};
  const getSignal=(name)=>{
    for(const plat of auditResults||[]){
      const s=plat.signals?.find(s=>s.signal===name);
      if(s) return s;
    }
    return null;
  };

  // GBP tasks
  const gbpVerified = gbmData?.status==="verified";
  const gbpClaimed  = gbmData?.gbpClaimed || gbpVerified;
  if(gbpVerified) auto.gbp_complete=true;
  if(gbpVerified) auto.gbp_categories=true;

  // Schema tasks
  const schema=getSignal("LocalBusiness Schema");
  if(schema?.status==="Pass") auto.schema_local=true;
  const faq=getSignal("FAQ Schema");
  if(faq?.status==="Pass") auto.schema_faq=true;

  // Citation tasks — derive from CITATION_SOURCES
  const sources=CITATION_SOURCES.map(s=>({...s,status:getCitationStatus(clientId,s.id)}));
  const tier1Sources=sources.filter(s=>s.tier===1);
  const tier2Sources=sources.filter(s=>s.tier===2);
  const allAccurate=sources.every(s=>s.status==="accurate");
  const tier1Accurate=tier1Sources.every(s=>s.status==="accurate");
  const tier2Accurate=tier2Sources.every(s=>s.status==="accurate");
  const missingCount=sources.filter(s=>s.status==="missing").length;
  const inaccurateCount=sources.filter(s=>s.status==="inaccurate").length;

  // cite_audit — only pass if fewer than 5 issues total
  if(missingCount+inaccurateCount < 5) auto.cite_audit=true;
  // cite_tier1 — all tier 1 sources accurate
  if(tier1Accurate) auto.cite_tier1=true;
  // cite_aggregators — all tier 2 accurate
  if(tier2Accurate) auto.cite_aggregators=true;
  // cite_sync — Vendasta sync done if most sources are accurate
  const pct=sources.filter(s=>s.status==="accurate").length/sources.length;
  if(pct>=0.80) auto.cite_sync=true;
  // cite_duplicates — check duplicate signal from audit
  const dupes=getSignal("Duplicate Listings");
  if(dupes?.status==="Pass") auto.cite_duplicates=true;

  // Reviews
  const reviews=getSignal("Review Signals");
  if(reviews?.status==="Pass") auto.review_respond=true;
  if(reviews?.status==="Pass"||reviews?.status==="Warning") auto.review_monthly=true;

  // Technical
  const llms=getSignal("llms.txt Present");
  if(llms?.status==="Pass") auto.tech_llmstxt=true;

  return auto;
}

function ChecklistTab({clientId, auditResults, gbmData, onChecklistChange}){
  const autoChecked = deriveAutoChecked(clientId, auditResults, gbmData);
  const [manualChecked,setManualChecked]=useState(()=>LS.get("checklist_"+clientId)||{});

  // Merge: auto wins for items it can confirm, manual handles the rest
  const checked={...manualChecked,...autoChecked};

  const toggle=async(id)=>{
    // Don't let user uncheck auto-confirmed items
    if(autoChecked[id]) return;
    const next={...manualChecked,[id]:!manualChecked[id]};
    setManualChecked(next);
    await ipc("save-checklist",{clientId,state:next});
    onChecklistChange?.({...next,...autoChecked});
  };

  const done=CHECKLIST_CATS.reduce((a,c)=>a+c.items.filter(i=>checked[i.id]).length,0);
  const delivered=CHECKLIST_CATS.reduce((a,c)=>a+c.items.filter(i=>checked[i.id]).reduce((b,i)=>b+i.price,0),0);
  const pct=Math.round((done/TOTAL_TASKS)*100);
  const autoCount=Object.keys(autoChecked).length;
  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      {autoCount>0&&(
        <div style={{background:`${T.teal}10`,border:`1px solid ${T.teal}33`,borderRadius:12,
          padding:"10px 18px",fontSize:12,color:T.teal,fontWeight:600,display:"flex",alignItems:"center",gap:8}}>
          🔍 {autoCount} task{autoCount!==1?"s":""} auto-confirmed from audit &amp; citation data — shown with a 🔒
        </div>
      )}
      {!auditResults&&(
        <div style={{background:`${T.gold}10`,border:`1px solid ${T.gold}33`,borderRadius:12,
          padding:"10px 18px",fontSize:12,color:T.gold,fontWeight:600}}>
          ⚡ Run an audit first to auto-confirm completed tasks from live data
        </div>
      )}
      <Card>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div>
            <span style={{fontSize:28,fontWeight:800,color:T.text}}>{done}/{TOTAL_TASKS}</span>
            <span style={{fontSize:14,color:T.ghost,marginLeft:8}}>{pct}% complete</span>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:22,fontWeight:800,color:T.teal}}>${delivered.toLocaleString()}</div>
            <div style={{fontSize:12,color:T.ghost}}>of ${TOTAL_VALUE.toLocaleString()} total value</div>
          </div>
        </div>
        <div style={{height:10,background:T.border,borderRadius:6,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${T.blue},${T.cyan})`,transition:"width 0.5s ease",borderRadius:6}}/>
        </div>
      </Card>
      {CHECKLIST_CATS.map(cat=>{
        const catDone=cat.items.filter(i=>checked[i.id]).length;
        return(
          <Card key={cat.name} style={{padding:0,overflow:"hidden"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"14px 24px",borderBottom:`1px solid ${T.border}`}}>
              <span style={{fontWeight:800,color:T.text}}>{cat.name}</span>
              <span style={{fontSize:12,color:T.ghost,background:T.input,padding:"3px 10px",borderRadius:20}}>{catDone}/{cat.items.length}</span>
            </div>
            {cat.items.map(item=>{
              const ck=!!checked[item.id];
              const isAuto=!!autoChecked[item.id];
              return(
                <div key={item.id} onClick={()=>toggle(item.id)}
                  style={{display:"flex",alignItems:"center",gap:14,padding:"12px 24px",
                    cursor:isAuto?"default":"pointer",
                    background:ck?`${T.teal}08`:"transparent",borderBottom:`1px solid ${T.border}`,transition:"background 0.12s"}}
                  onMouseEnter={e=>{if(!ck&&!isAuto)e.currentTarget.style.background=T.input;}}
                  onMouseLeave={e=>e.currentTarget.style.background=ck?`${T.teal}08`:"transparent"}>
                  <div className={ck&&!isAuto?"check-pop":""}
                    style={{width:22,height:22,borderRadius:6,
                      border:`2px solid ${ck?"transparent":T.border}`,
                      background:ck?isAuto?`linear-gradient(135deg,${T.teal},#48BB78)`:`linear-gradient(135deg,${T.blue},${T.cyan})`:"transparent",
                      display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all 0.15s"}}>
                    {ck&&<span style={{color:T.white,fontSize:isAuto?12:11,fontWeight:800}}>{isAuto?"🔒":"✓"}</span>}
                  </div>
                  <span style={{flex:1,fontSize:13,fontWeight:600,color:ck?T.ghost:T.text,textDecoration:ck?"line-through":"none"}}>{item.task}</span>
                  {isAuto&&ck&&<span style={{fontSize:10,color:T.teal,fontWeight:700,background:`${T.teal}15`,padding:"2px 7px",borderRadius:6}}>AUTO</span>}
                  <span style={{fontSize:11,color:T.ghost,background:T.input,padding:"3px 10px",borderRadius:8}}>{item.time}</span>
                  <span style={{fontSize:13,fontWeight:700,minWidth:50,textAlign:"right",color:ck?`${T.teal}66`:T.teal,textDecoration:ck?"line-through":"none"}}>${item.price}</span>
                </div>
              );
            })}
          </Card>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────
// IN-APP REPORT VIEWER
// ─────────────────────────────────────────────
function buildReportHTML(r, client){
  const completed = r.completedTasks || [];
  const grade = r.score>=90?"A":r.score>=80?"B":r.score>=70?"C":r.score>=60?"D":"F";
  const gradeCol = r.score>=80?"#01B574":r.score>=60?"#FFB547":"#E31A1A";
  const month = `${r.month} ${r.year}`;
  const tasks = completed.map(t=>`<li style="margin-bottom:4px">✓ ${t.replace(/_/g," ")}</li>`).join("");
  const circumference = 2 * Math.PI * 54;
  const dash = circumference - (r.score / 100) * circumference;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
  *{margin:0;padding:0;box-sizing:border-box;}
  body{font-family:'Plus Jakarta Sans',sans-serif;background:#0D1528;color:#fff;padding:40px;}
  .header{background:linear-gradient(135deg,#4318FF,#0075FF);border-radius:20px;padding:36px;margin-bottom:28px;position:relative;overflow:hidden;}
  .header::after{content:'';position:absolute;right:-60px;top:-60px;width:240px;height:240px;border-radius:50%;background:rgba(255,255,255,0.05);}
  .header::before{content:'';position:absolute;right:60px;bottom:-80px;width:180px;height:180px;border-radius:50%;background:rgba(255,255,255,0.04);}
  .brand{font-size:12px;font-weight:700;letter-spacing:2px;color:rgba(255,255,255,0.6);margin-bottom:8px;}
  .client-name{font-size:28px;font-weight:800;margin-bottom:4px;}
  .sub{font-size:14px;color:rgba(255,255,255,0.65);}
  .score-row{display:flex;align-items:center;gap:32px;margin-top:24px;}
  .score-ring{position:relative;width:120px;height:120px;flex-shrink:0;}
  .score-ring svg{transform:rotate(-90deg);}
  .score-val{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;}
  .score-val .num{font-size:28px;font-weight:800;}
  .score-val .lbl{font-size:10px;color:rgba(255,255,255,0.5);letter-spacing:1px;}
  .grade-badge{font-size:48px;font-weight:800;color:${gradeCol};line-height:1;}
  .delta{font-size:13px;color:rgba(255,255,255,0.7);margin-top:4px;}
  .section{background:rgba(255,255,255,0.06);border-radius:16px;padding:24px;margin-bottom:20px;}
  .section-title{font-size:11px;font-weight:700;letter-spacing:1.5px;color:rgba(255,255,255,0.5);margin-bottom:16px;text-transform:uppercase;}
  .row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid rgba(255,255,255,0.07);font-size:13px;}
  .row:last-child{border-bottom:none;}
  .row .k{color:rgba(255,255,255,0.55);}
  .row .v{font-weight:600;}
  .task-list{list-style:none;column-count:2;column-gap:24px;font-size:12px;color:rgba(255,255,255,0.75);line-height:1.9;}
  .footer{text-align:center;font-size:11px;color:rgba(255,255,255,0.3);margin-top:32px;letter-spacing:1px;}
  .no-tasks{font-size:13px;color:rgba(255,255,255,0.4);font-style:italic;}
</style>
</head>
<body>
  <div class="header">
    <div class="brand">SHARK AI · GEO INTELLIGENCE PLATFORM</div>
    <div class="client-name">${client.name}</div>
    <div class="sub">${client.domain}${client.city?" · "+client.city:""} · ${month} Report</div>
    <div class="score-row">
      <div class="score-ring">
        <svg width="120" height="120" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="8"/>
          <circle cx="60" cy="60" r="54" fill="none" stroke="#4318FF" stroke-width="8"
            stroke-dasharray="${circumference}" stroke-dashoffset="${dash}"
            stroke-linecap="round"/>
        </svg>
        <div class="score-val"><span class="num">${r.score}</span><span class="lbl">GEO SCORE</span></div>
      </div>
      <div>
        <div class="grade-badge">${grade}</div>
        <div class="delta">Generated ${r.date}</div>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Report Details</div>
    <div class="row"><span class="k">Client</span><span class="v">${client.name}</span></div>
    <div class="row"><span class="k">Domain</span><span class="v">${client.domain}</span></div>
    <div class="row"><span class="k">City</span><span class="v">${client.city||"—"}</span></div>
    <div class="row"><span class="k">Business Type</span><span class="v">${client.businessType||"—"}</span></div>
    <div class="row"><span class="k">Report Period</span><span class="v">${month}</span></div>
    <div class="row"><span class="k">Report Type</span><span class="v">${r.type||"Monthly GEO Performance Report"}</span></div>
    <div class="row"><span class="k">Generated</span><span class="v">${r.date}</span></div>
  </div>

  <div class="section">
    <div class="section-title">Completed GEO Tasks This Month</div>
    ${completed.length>0
      ? `<ul class="task-list">${tasks}</ul>`
      : `<div class="no-tasks">No tasks marked complete for this period.</div>`}
  </div>

  <div class="footer">SHARK AI · Shark Branding Solutions · St. Petersburg, FL · sharkbrandingsolutions.com</div>
</body>
</html>`;
}

// ─────────────────────────────────────────────
// REPORTS TAB
// ─────────────────────────────────────────────
function ReportsTab({client,checkedState}){
  const [reports,setReports]=useState(()=>LS.get("reports_"+client.id)||[]);
  const [gen,setGen]=useState(false);
  const [rtype,setRtype]=useState("Monthly GEO Performance Report");
  const [viewing,setViewing]=useState(null);

  const generate=async()=>{
    setGen(true);
    const completedTasks=Object.entries(checkedState||{}).filter(([,v])=>v).map(([k])=>k);
    const result=await ipc("generate-report",{clientId:client.id,client:client.name,domain:client.domain,
      month:new Date().toLocaleString("default",{month:"long",year:"numeric"}),
      score:client.score,completed:JSON.stringify(completedTasks)});
    const report={
      id:Date.now().toString(),
      month:new Date().toLocaleString("default",{month:"long"}),
      year:new Date().getFullYear(),
      score:client.score,
      date:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}),
      size:"2.1 MB",
      path:result?.path||null,
      type:rtype,
      completedTasks,
    };
    await ipc("save-report",{clientId:client.id,report});
    setReports(prev=>[report,...prev]);
    setViewing(report);
    setGen(false);
  };

  if(viewing){
    const html=buildReportHTML(viewing,client);
    return(
      <div style={{display:"flex",flexDirection:"column",height:"100%",gap:0}}>
        <div style={{display:"flex",alignItems:"center",gap:12,padding:"12px 0",flexShrink:0}}>
          <Btn variant="ghost" onClick={()=>setViewing(null)}>← Back to Reports</Btn>
          <span style={{fontSize:13,fontWeight:700,color:T.text}}>{viewing.month} {viewing.year} — {viewing.type||"Monthly GEO Report"}</span>
          <div style={{flex:1}}/>
          <Btn sm variant="primary" onClick={()=>{
            const blob=new Blob([html],{type:"text/html"});
            const a=document.createElement("a");
            a.href=URL.createObjectURL(blob);
            a.download=`${client.name.replace(/\s+/g,"-")}-${viewing.month}-${viewing.year}-GEO-Report.html`;
            a.click();
          }}>⬇ Save as File</Btn>
        </div>
        <div style={{flex:1,borderRadius:16,overflow:"hidden",border:`1px solid ${T.border}`}}>
          <iframe
            srcDoc={html}
            style={{width:"100%",height:"100%",border:"none"}}
            title="Report Preview"
          />
        </div>
      </div>
    );
  }

  return(
    <div style={{display:"flex",flexDirection:"column",gap:20}}>
      <Card>
        <div style={{fontWeight:800,color:T.text,marginBottom:16}}>Generate New Report</div>
        <div style={{display:"flex",gap:12}}>
          <select value={rtype} onChange={e=>setRtype(e.target.value)}
            style={{flex:1,background:T.input,border:`1px solid ${T.border}`,borderRadius:12,padding:"10px 14px",fontSize:13,color:T.text,fontFamily:"inherit",outline:"none"}}>
            <option>Monthly GEO Performance Report</option>
            <option>Platform-Specific Audit</option>
            <option>Citation Analysis</option>
            <option>Schema Coverage Report</option>
          </select>
          <Btn variant="primary" onClick={generate} disabled={gen}>
            {gen?<span className="spin" style={{width:14,height:14,border:`2px solid rgba(255,255,255,0.3)`,borderTopColor:T.white,borderRadius:"50%"}}/>:"📄"}
            {gen?" Generating...":" Generate"}
          </Btn>
        </div>
      </Card>
      <Card style={{padding:0,overflow:"hidden"}}>
        <div style={{padding:"16px 24px",borderBottom:`1px solid ${T.border}`}}>
          <span style={{fontWeight:800,color:T.text}}>Recent Reports</span>
        </div>
        {reports.length===0&&(
          <div style={{padding:40,textAlign:"center",color:T.ghost,fontSize:13}}>
            No reports yet — generate your first one above.
          </div>
        )}
        {reports.map(r=>(
          <div key={r.id} style={{display:"flex",alignItems:"center",gap:16,padding:"14px 24px",
            borderBottom:`1px solid ${T.border}`,transition:"background 0.12s",cursor:"pointer"}}
            onClick={()=>setViewing(r)}
            onMouseEnter={e=>e.currentTarget.style.background=T.input}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
            <ScoreRing score={r.score} size={48} sw={4}/>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:13,color:T.text}}>{r.month} {r.year} — {r.type||"Monthly GEO Report"}</div>
              <div style={{fontSize:11,color:T.ghost,marginTop:2}}>📅 {r.date} · {r.completedTasks?.length||0} tasks completed</div>
            </div>
            <Btn sm variant="primary" onClick={e=>{e.stopPropagation();setViewing(r);}}>👁 View Report</Btn>
          </div>
        ))}
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────
// CLIENT DASHBOARD
// ─────────────────────────────────────────────
const TABS=["OVERVIEW","AUDIT","CHECKLIST","REPORTS"];

function ClientDashboard({client,onBack,onClientUpdate}){
  const [tab,setTab]=useState("OVERVIEW");
  const [deploying,setDeploying]=useState(false);
  const [deployPct,setDeployPct]=useState(0);
  const [deployStep,setDeployStep]=useState("");
  const [auditing,setAuditing]=useState(false);
  const [syncing,setSyncing]=useState(false);
  const [gbmData,setGbmDataRaw]=useState(()=>LS.get("gbm_"+client.id)||null);
  const setGbmData=(d)=>{setGbmDataRaw(d);LS.set("gbm_"+client.id,d);};
  const [checkedState,setCheckedState]=useState({});
  const [auditResults,setAuditResults]=useState(null);
  const [editingCity,setEditingCity]=useState(false);
  const [cityVal,setCityVal]=useState(client.city||"");

  const saveCity=async()=>{
    const updated={...client,city:cityVal};
    await ipc("save-client",updated);
    onClientUpdate?.(updated);
    setEditingCity(false);
  };

  const [geoData,setGeoData]=useState(null);
  const runAudit=async()=>{
    setAuditing(true);
    setTab("AUDIT");
    // Run real GEO checks + mock platform signals in parallel
    const noGBP = !gbmData?.gbpClaimed && (!cityVal && !client.city);
    const [geoResult, _] = await Promise.allSettled([
      runGeoAudit(client),
      new Promise(r=>setTimeout(r,1800)),
    ]);
    const geo = geoResult.status==="fulfilled" ? geoResult.value : null;
    if (geo) setGeoData(geo);
    const results=AUDIT_DATA.map(platform=>({
      ...platform,
      signals:platform.signals.map(s=>{
        let status;
        if(s.signal==="Knowledge Panel") status="Fail";
        else if(s.signal==="llms.txt Present") status= geo?.llmsData ? "Pass" : geo?.llmsGenerated ? "Fail" : "Fail";
        else if(s.signal==="Wikipedia / Wikidata") status= geo?.wikiUrl ? "Pass" : "Fail";
        else if(s.signal==="Brand Entity Recognition"&&noGBP) status="Fail";
        else { const roll=Math.random(); status=roll<0.35?"Fail":roll<0.6?"Warning":"Pass"; }
        return{ signal:s.signal, impact:s.impact, status, finding:s.findings[status] };
      })
    }));
    setAuditResults(results);
    setAuditing(false);
  };

  const gmbSync=async()=>{
    setSyncing(true);
    await new Promise(r=>setTimeout(r,1200));
    // Use the city the user has set — don't invent an address
    const city = cityVal || client.city || "";
    const hasGBP = !!city; // treat as having a GBP only if city is set
    setGbmData({
      name: client.name,
      city: city,
      // Only show address if we have a city — don't fabricate one
      address: city ? `${client.name}, ${city}` : null,
      phone: null, // unknown until real API connected
      verified: false, // not verified — no GMB claimed yet
      gbpClaimed: false,
      rating: null,
      reviews: null,
      category: client.businessType,
      status: city ? "unverified_listing" : "no_gbp",
    });
    setSyncing(false);
  };
  const deployGTM=async()=>{
    setDeploying(true);setDeployPct(0);
    const steps=[{p:20,l:"Connecting to GTM..."},{p:40,l:"Validating schema..."},{p:60,l:"Deploying tags..."},{p:80,l:"Publishing changes..."},{p:100,l:"Complete!"}];
    for(const s of steps){await new Promise(r=>setTimeout(r,700));setDeployPct(s.p);setDeployStep(s.l);}
    setTimeout(()=>setDeploying(false),900);
  };

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{background:T.white,borderBottom:`1px solid ${T.border}`,padding:"14px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <button onClick={onBack} style={{background:T.input,border:"none",borderRadius:10,width:34,height:34,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"inherit"}}>←</button>
            <div>
              <div style={{fontWeight:800,fontSize:16,color:T.text}}>{client.name}</div>
              <div style={{display:"flex",alignItems:"center",gap:6,marginTop:2}}>
                <span style={{fontSize:12,color:T.ghost}}>{client.domain}</span>
                {editingCity?(
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <input value={cityVal} onChange={e=>setCityVal(e.target.value)}
                      autoFocus
                      onKeyDown={e=>{if(e.key==="Enter")saveCity();if(e.key==="Escape")setEditingCity(false);}}
                      style={{fontSize:12,color:T.text,background:T.input,border:`1px solid ${T.blue}`,
                        borderRadius:6,padding:"2px 8px",outline:"none",width:160,fontFamily:"inherit"}}/>
                    <button onClick={saveCity} style={{fontSize:11,color:T.white,background:T.blue,border:"none",
                      borderRadius:6,padding:"2px 8px",cursor:"pointer",fontFamily:"inherit",fontWeight:700}}>Save</button>
                    <button onClick={()=>setEditingCity(false)} style={{fontSize:11,color:T.ghost,background:T.input,
                      border:"none",borderRadius:6,padding:"2px 8px",cursor:"pointer",fontFamily:"inherit"}}>✕</button>
                  </div>
                ):(
                  <button onClick={()=>setEditingCity(true)}
                    style={{fontSize:11,color:cityVal?T.blue:T.ghost,background:"transparent",border:"none",
                      cursor:"pointer",display:"flex",alignItems:"center",gap:3,fontFamily:"inherit",padding:0}}>
                    {cityVal?`· ${cityVal}`:"+ Add City"} ✎
                  </button>
                )}
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:8}}>
            <Btn sm onClick={gmbSync} disabled={syncing}>{syncing?"⟳ Syncing...":"⟳ GMB Sync"}</Btn>
            <Btn sm onClick={runAudit} disabled={auditing}>{auditing?"⟳ Auditing...":"▶ Run Audit"}</Btn>
            <Btn sm onClick={deployGTM} disabled={deploying||!client.gtm} title={!client.gtm?"Add GTM Container ID to client first":""}>Deploy GTM{!client.gtm&&<span style={{marginLeft:5,fontSize:9,opacity:0.7}}>âš </span>}</Btn>
            <Btn sm variant="primary" onClick={()=>setTab("REPORTS")}>📄 Gen Report</Btn>
          </div>
        </div>
        {deploying&&(
          <div style={{marginTop:12}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:6}}>
              <span style={{color:T.ghost}}>{deployStep}</span>
              <span style={{color:T.blue,fontWeight:700}}>{deployPct}%</span>
            </div>
            <div style={{height:6,background:T.border,borderRadius:4,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${deployPct}%`,background:`linear-gradient(90deg,${T.blue},${T.cyan})`,transition:"width 0.5s ease",borderRadius:4}}/>
            </div>
          </div>
        )}
      </div>
      <div style={{background:T.white,borderBottom:`1px solid ${T.border}`,padding:"0 24px",flexShrink:0,display:"flex",gap:28}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)}
            style={{padding:"14px 0",border:"none",background:"transparent",cursor:"pointer",fontSize:12,fontWeight:700,
              letterSpacing:1,fontFamily:"inherit",color:tab===t?T.blue:T.ghost,
              borderBottom:tab===t?`2px solid ${T.blue}`:"2px solid transparent",transition:"all 0.15s"}}>
            {t}
          </button>
        ))}
      </div>
      <div style={{flex:1,overflow:"auto",padding:28,background:T.bg}}>
        {tab==="OVERVIEW"  &&<OverviewTab client={client} gbmData={gbmData} setGbmData={setGbmData} syncing={syncing} onSync={gmbSync} onTabSwitch={setTab}/>}
        {tab==="AUDIT"     &&<AuditTab auditResults={auditResults} auditing={auditing} clientId={client.id} geoData={geoData} clientGtm={client.gtm}/>}
        {tab==="CHECKLIST" &&<ChecklistTab clientId={client.id} auditResults={auditResults} gbmData={gbmData} onChecklistChange={setCheckedState}/>}
        {tab==="REPORTS"   &&<ReportsTab client={client} checkedState={checkedState}/>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SCHEMA INJECTOR
// ─────────────────────────────────────────────
function SchemaInjector({clients}){
  const [month,setMonth]=useState(()=>new Date().toLocaleString("default",{month:"long",year:"numeric"}));
  const [selected,setSelected]=useState(new Set());
  const [statuses,setStatuses]=useState({});
  const [running,setRunning]=useState(false);
  const toggleAll=()=>setSelected(selected.size===clients.length?new Set():new Set(clients.map(c=>c.id)));
  const toggle=id=>{const n=new Set(selected);n.has(id)?n.delete(id):n.add(id);setSelected(n);};
  const inject=async()=>{
    if(!selected.size||running)return;setRunning(true);
    for(const id of selected){
      setStatuses(s=>({...s,[id]:"injecting"}));
      const r=await ipc("inject-schema",{clientId:id,month});
      setStatuses(s=>({...s,[id]:r?.success?"done":"error"}));
    }
    setRunning(false);
  };
  const statusEl=id=>{const s=statuses[id];
    if(s==="injecting")return<span style={{color:T.blue,fontSize:12,fontWeight:700,display:"flex",alignItems:"center",gap:5}}><span className="spin" style={{width:12,height:12,border:`2px solid ${T.blue}44`,borderTopColor:T.blue,borderRadius:"50%"}}/> Injecting…</span>;
    if(s==="done")return<span style={{color:T.teal,fontSize:12,fontWeight:700}}>✓ Done</span>;
    if(s==="error")return<span style={{color:T.red,fontSize:12,fontWeight:700}}>✗ Error</span>;
    return null;
  };
  return(
    <div style={{flex:1,padding:28,overflow:"auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:24}}>
        <div>
          <div style={{fontSize:20,fontWeight:800,color:T.text}}>Schema Injector</div>
          <div style={{fontSize:13,color:T.ghost,marginTop:4}}>One-click monthly schema injection across all clients via GTM</div>
        </div>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <Inp value={month} onChange={setMonth} style={{width:180}}/>
          <Btn variant="primary" onClick={inject} disabled={!selected.size||running}>⚡ Inject {selected.size} Selected</Btn>
        </div>
      </div>
      <Card style={{padding:0,overflow:"hidden"}}>
        <div onClick={toggleAll} style={{display:"flex",alignItems:"center",gap:14,padding:"14px 24px",borderBottom:`1px solid ${T.border}`,cursor:"pointer"}}
          onMouseEnter={e=>e.currentTarget.style.background=T.input}
          onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
          <div style={{width:22,height:22,borderRadius:6,
            border:`2px solid ${selected.size===clients.length?"transparent":T.border}`,
            background:selected.size===clients.length?`linear-gradient(135deg,${T.blue},${T.cyan})`:selected.size>0?`${T.blue}55`:"transparent",
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            {selected.size===clients.length&&<span style={{color:T.white,fontSize:11,fontWeight:800}}>✓</span>}
            {selected.size>0&&selected.size<clients.length&&<div style={{width:10,height:2,background:T.white}}/>}
          </div>
          <span style={{fontSize:13,fontWeight:700,color:T.text}}>SELECT ALL ({clients.length} clients)</span>
          <span style={{fontSize:12,color:T.ghost,marginLeft:"auto"}}>{selected.size} selected</span>
        </div>
        {clients.map(c=>{const sel=selected.has(c.id);return(
          <div key={c.id} onClick={()=>toggle(c.id)}
            style={{display:"flex",alignItems:"center",gap:14,padding:"12px 24px",cursor:"pointer",
              background:sel?`${T.blue}07`:"transparent",borderBottom:`1px solid ${T.border}`,transition:"background 0.12s"}}
            onMouseEnter={e=>{if(!sel)e.currentTarget.style.background=T.input;}}
            onMouseLeave={e=>e.currentTarget.style.background=sel?`${T.blue}07`:"transparent"}>
            <div style={{width:22,height:22,borderRadius:6,
              border:`2px solid ${sel?"transparent":T.border}`,
              background:sel?`linear-gradient(135deg,${T.blue},${T.cyan})`:"transparent",
              display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              {sel&&<span style={{color:T.white,fontSize:11,fontWeight:800}}>✓</span>}
            </div>
            <div style={{flex:1}}>
              <span style={{fontSize:13,fontWeight:600,color:T.text}}>{c.name}</span>
              <span style={{fontSize:12,color:T.ghost,marginLeft:8}}>{c.domain} · {c.businessType}</span>
            </div>
            {statusEl(c.id)}
          </div>
        );})}
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────
// SETTINGS
// ─────────────────────────────────────────────
function SettingsView(){
  const [url,setUrl]=useState(()=>LS.get("settings")?.webhookUrl||"");
  const [note,setNote]=useState(()=>LS.get("settings")?.note||"");
  const [saved,setSaved]=useState(false);
  const save=async()=>{await ipc("save-settings",{webhookUrl:url,note});setSaved(true);setTimeout(()=>setSaved(false),2000);};
  const env=[["Brave Proxy","https://proxy.brave.com/v1"],["OAuth Callback","sharkgeo://oauth/callback"],["Intent Webhook",url||"Not configured"],["Electron","v28.1.3"]];
  return(
    <div style={{flex:1,padding:28,overflow:"auto"}}>
      <div style={{maxWidth:680}}>
        <div style={{fontSize:20,fontWeight:800,color:T.text,marginBottom:24}}>Settings</div>
        <Card style={{marginBottom:20}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:6}}>Webhook URL</div>
          <div style={{fontSize:13,color:T.ghost,marginBottom:18}}>Configure your webhook endpoint for intent signals. Use ngrok for local dev testing.</div>
          <div style={{display:"flex",flexDirection:"column",gap:14}}>
            <div><div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1.1,marginBottom:6}}>URL</div><Inp value={url} onChange={setUrl} placeholder="https://your-webhook.ngrok.io/api/intent"/></div>
            <div><div style={{fontSize:11,fontWeight:700,color:T.ghost,letterSpacing:1.1,marginBottom:6}}>NOTE</div><Inp value={note} onChange={setNote} placeholder="Optional description"/></div>
            <div><Btn variant={saved?"success":"primary"} onClick={save}>{saved?"✓ Saved":"Save Settings"}</Btn></div>
          </div>
        </Card>
        <Card style={{marginBottom:20}}>
          <div style={{fontWeight:800,color:T.text,marginBottom:16}}>Environment</div>
          {env.map(([k,v])=>(
            <div key={k} style={{display:"flex",justifyContent:"space-between",padding:"10px 0",borderBottom:`1px solid ${T.border}`}}>
              <span style={{fontSize:13,color:T.ghost}}>{k}</span>
              <span style={{fontSize:13,fontWeight:600,color:T.text,fontFamily:"monospace"}}>{v}</span>
            </div>
          ))}
        </Card>
        <div style={{fontSize:12,color:T.ghost,textAlign:"center"}}>
          Press <kbd style={{background:T.input,border:`1px solid ${T.border}`,borderRadius:5,padding:"2px 7px",fontSize:11,fontFamily:"monospace"}}>F10</kbd> to show/hide window
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────
const SEED=[
  {id:"1",name:"Oakwood Dental",domain:"oakwooddental.com",businessType:"Healthcare",city:"Tampa, FL",score:87,grade:"B",lastUpdated:"Mar 3, 2026"},
  {id:"2",name:"Summit Legal Partners",domain:"summitlegal.com",businessType:"Legal Services",city:"St. Petersburg, FL",score:92,grade:"A",lastUpdated:"Mar 2, 2026"},
  {id:"3",name:"Pacific Coast Plumbing",domain:"pcplumbing.net",businessType:"Home Services",city:"Wesley Chapel, FL",score:74,grade:"C",lastUpdated:"Feb 28, 2026"},
  {id:"4",name:"Greenleaf Landscaping",domain:"greenleaflandscape.com",businessType:"Home Services",city:"Lutz, FL",score:68,grade:"D",lastUpdated:"Feb 25, 2026"},
  {id:"5",name:"Metro Auto Group",domain:"metroautogroup.com",businessType:"Automotive",city:"Land O' Lakes, FL",score:81,grade:"B",lastUpdated:"Mar 1, 2026"},
  {id:"6",name:"Worthington Agency",domain:"worthingtonagency.com",businessType:"Insurance",city:"Wesley Chapel, FL",score:72,grade:"C",lastUpdated:"Mar 7, 2026"},
];

export default function App(){
  const [nav,setNav]=useState("clients");
  const [clients,setClients]=useState(()=>{const s=LS.get("clients");return Array.isArray(s)&&s.length?s:SEED;});
  const [selected,setSelected]=useState(null);
  const [user,setUser]=useState(()=>LS.get("user")||null);

  useEffect(()=>{ipc("get-clients").then(cs=>{if(Array.isArray(cs)&&cs.length)setClients(cs);});},[]);

  const addClient=async(form)=>{
    const score=Math.floor(Math.random()*35)+55;
    const c={id:Date.now().toString(),...form,score,grade:gradeFromScore(score),
      city:form.city||"",
      lastUpdated:new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})};
    await ipc("save-client",c);
    setClients(prev=>[c,...prev]);
  };
  const deleteClient=async(id)=>{
    await ipc("delete-client",id);
    setClients(prev=>(Array.isArray(prev)?prev:[]).filter(c=>c.id!==id));
    if(selected?.id===id)setSelected(null);
  };
  const updateClient=(updated)=>{
    setClients(prev=>prev.map(c=>c.id===updated.id?updated:c));
    if(selected?.id===updated.id)setSelected(updated);
  };
  const signIn=(u)=>{setUser(u);LS.set("user",u);};
  const signOut=()=>{setUser(null);LS.set("user",null);};

  const getTitle=()=>selected?selected.name:{clients:"Clients",schema:"Schema Injector",settings:"Settings"}[nav];
  const getBreadcrumb=()=>selected?`Pages / Clients / ${selected.name}`:`Pages / ${getTitle()}`;

  return(
    <>
      <style>{GS}</style>
      <div style={{display:"flex",height:"100vh",width:"100vw",background:T.bg,overflow:"hidden"}}>
        <Sidebar activeNav={nav} onNavChange={n=>{setNav(n);setSelected(null);}}/>
        <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <Header title={getTitle()} breadcrumb={getBreadcrumb()} user={user} onSignIn={signIn} onSignOut={signOut}/>
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {nav==="clients"&&!selected&&<ClientList clients={clients} onSelect={setSelected} onAdd={addClient} onDelete={deleteClient}/>}
            {nav==="clients"&&selected&&<ClientDashboard client={selected} onBack={()=>setSelected(null)} onClientUpdate={updateClient}/>}
            {nav==="schema"&&<SchemaInjector clients={clients}/>}
            {nav==="settings"&&<SettingsView/>}
          </div>
        </div>
      </div>
    </>
  );
}